import React, { useState } from "react";
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Bubble } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";

import { ReferenceArea } from "recharts";
import { numberFormatterForCPGraph, setTicks } from "../../services/utils";
import { numberFormatter } from "../../utils/formatter";
import { Box } from "@mui/material";
import useGraphFontSize from "../../hooks/useGraphFontSize";

ChartJS.register(
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    zoomPlugin,
    annotationPlugin
);

let maxx = 20;
let minn = 20;
const zoomOptions = {
    pan: {
        enabled: true,
        mode: "xy",
        modifierKey: "ctrl",
    },
    zoom: {
        mode: "xy",
        drag: {
            enabled: true,
            borderColor: "rgb(54, 162, 235)",
            borderWidth: 1,
            backgroundColor: "rgba(54, 162, 235, 0.3)",
        },
    },
};

type Props = {
    selectedCustomerData?: PricingGraphCustomer,
    customerData: PricingGraphCustomer[],
    minYAxis: number,
    maxYAxis: number,
}
const BubbleChart = ({ customerData, maxYAxis, minYAxis, selectedCustomerData }: Props) => {
    let graphData: any = [];
    let backgroundColor: any = [];
    let borderColor: any = [];
    const fontSize = useGraphFontSize()

    customerData?.forEach((d: PricingGraphCustomer) => {
        graphData.push({ x: maxx, y: d.price, r: 10 });
        if (d?.customer === selectedCustomerData?.customer) {
            backgroundColor.push("#e7290b");
            borderColor.push("#fff");
        } else {
            backgroundColor.push("rgba(132,187,193,0.5)");
            borderColor.push("rgba(132,187,193,0.5)");
        }
    });

    const data: any = {
        datasets: [
            {
                data: graphData,
                backgroundColor: backgroundColor,
                borderColor: borderColor,
            },
        ],
    };

    const options: any = {
        maintainAspectRatio: false,
        plugins: {
            annotation: {
                annotations: {
                    line1: {
                        display:
                            selectedCustomerData?.executedPrice !== null
                                ? true
                                : false,
                        type: "line",
                        drawTime: "beforeDatasetsDraw",
                        yMin: selectedCustomerData?.executedPrice,
                        yMax: selectedCustomerData?.executedPrice,
                        xMin: 4,
                        xMax: 35,
                        borderWidth: 3,
                        borderColor: "rgba(255, 99, 132)",
                        borderDash: [8, 8],
                        enter: function ({ element }: { element: any }) {
                            element.label.options.display = true;
                            return true;
                        },
                        leave: function ({ element }: { element: any }) {
                            element.label.options.display = false;
                            return true;
                        },
                        label: {
                            display: false,
                            xAdjust: 30,
                            content:
                                "Target Price:" +
                                selectedCustomerData?.executedPrice,
                            position: "end",
                            font: {
                                size: 8,
                            },
                        },
                    },
                    box1: {
                        type: "box",
                        drawTime: "beforeDatasetsDraw",
                        xMin: 4,
                        xMax: 35,
                        borderWidth: 0,
                        borderColor: "black",
                        backgroundColor: "rgba(102,102,102,0.2)",
                    },
                },
            },
            zoom: zoomOptions,

            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        let element = customerData[context.dataIndex];
                        return ["Customer: " +
                            element.customer.split(" ").join("\n"),
                        "Project Flag : " + element.project_flag,
                        "Price: $" + numberFormatter(element.price, 2),
                        "Qty Purchased: " +
                        numberFormatter(+element.volume),
                        "Revenue: " +
                        "$" +
                        numberFormatter(element.revenue, 2),
                        ];
                    },

                    labelTextColor: function (context: any) {
                        return "#d3d3d3";
                    },
                },
                displayColors: true,
                backgroundColor: "#333333",
            },
            legend: {
                display: false,
                labels: {
                    usePointStyle: true,
                    boxWidth: 5,
                    boxHeight: 5,
                    color: "yellow",
                },
                position: "bottom",
                align: "end",
            },
        },
        scales: {
            x: {
                offset: true,
                display: false,
                border: {
                    color: "black",
                },
                title: {
                    display: true,
                    text: "Vol/Drop",
                    color: "black",
                    font: {
                        size: fontSize,
                    },
                },
                grid: {
                    display: false,
                },
                ticks: {
                    display: false,
                    min: minn,
                    max: maxx,
                    maxTicksLimit: 4,
                    callback: function (val: any) {
                        return numberFormatterForCPGraph(Math.exp(val), 0);
                    },
                    color: "black",
                    font: {
                        size: fontSize,
                        weight: 400

                    },
                },
            },
            y: {
                border: {
                    color: "black",
                    width: 1,
                },
                type: "linear",
                suggestedMin: minYAxis,
                suggestedMax: maxYAxis,
                beginAtZero: false,
                title: {
                    display:
                        true,
                    color: "black",
                    text: "Price/Unit($)",
                    font: {
                        size: fontSize,
                        weight: 400
                    },
                },
                grid: {
                    display: false,
                },
                ticks: {
                    color: "black",
                    display:
                        true,
                    autoSkip: true,
                    maxTicksLimit: 5,

                    callback: function (value: any) {
                        return "$" + value.toFixed(2);
                    },
                    font: {
                        size: fontSize,
                        weight: 400
                    },
                },
            },
        },
    };

    const chartRef: any = React.useRef(null);
    const handleResetZoom = () => {
        if (chartRef && chartRef.current) {
            chartRef.current.resetZoom();
        }
    };

    return (
        <div className="h-full text-center flex flex-col items-center">
            <Box component={"button"}
                onClick={handleResetZoom}
                type="button"
                className="btn-text !text-white !bg-[#86BC25] h-[5vh] px-[1vw] py-[0.5vh] focus:bg-[#86BC25] hover:bg-[#86BC25] disabled:bg-[#86BC25] disabled:opacity-[0.6] w-[13vw] rounded-[5px]"> Reset Zoom
            </Box>
            <Bubble
                ref={chartRef}
                options={options}
                data={data}
            />
        </div >
    );
}

export default BubbleChart